<template>
  <el-dialog
    :visible.sync="dialogVisible"
    :append-to-body="true"
    :close-on-click-modal="false"
    custom-class="product-finder"
    width="820px"
    top="7vh"
    @open="handleOpen"
    @closed="handleClosed"
  >
    <span slot="title" class="finder-title">
      <i class="el-icon-search"></i>
      <span>{{ $t("productFinder.title") }}</span>
    </span>

    <div class="finder">
      <p class="finder__lead">{{ $t("productFinder.lead") }}</p>
      <el-input
        ref="search"
        v-model="query"
        size="medium"
        clearable
        prefix-icon="el-icon-search"
        :placeholder="$t('productFinder.searchPlaceholder')"
        @input="onInput"
        @clear="runSearch"
        @keyup.enter.native="runSearch"
      ></el-input>

      <div v-loading="loading" class="finder__results">
        <!-- 初始态 -->
        <div v-if="!touched" class="finder__hint">
          <i class="el-icon-collection"></i>
          <span>{{ $t("productFinder.startHint") }}</span>
        </div>

        <!-- 结果 -->
        <div v-else-if="results.length" class="finder-grid">
          <article v-for="item in results" :key="item.id" class="finder-card">
            <div class="finder-card__img">
              <el-image :src="item.url" fit="contain">
                <div slot="error" class="finder-card__ph"><i class="el-icon-picture-outline"></i></div>
              </el-image>
            </div>
            <div class="finder-card__body">
              <div class="finder-card__name" :title="item.name">{{ item.name || "-" }}</div>
              <div class="finder-card__meta">
                <span class="finder-card__price">{{ formatPrice(item.costPrice) }}</span>
                <span v-if="item.goodsBarcode" class="finder-card__code">{{ item.goodsBarcode }}</span>
              </div>
            </div>
            <el-button
              v-if="!isAdded(item)"
              type="primary"
              size="mini"
              :loading="obtainingId === item.id"
              @click="useThis(item)"
            >
              {{ $t("productFinder.useThis") }}
            </el-button>
            <span v-else class="finder-card__added"><i class="el-icon-check"></i> {{ $t("productHub.added") }}</span>
          </article>
        </div>

        <!-- 无结果 -->
        <div v-else class="finder__empty">
          <i class="el-icon-box"></i>
          <p>{{ $t("productFinder.noMatch", { q: lastQuery }) }}</p>
          <span class="finder__empty-sub">{{ $t("productFinder.noMatchSub") }}</span>
        </div>
      </div>
    </div>

    <span slot="footer" class="finder-footer">
      <span class="finder-footer__note">{{ $t("productFinder.createNote") }}</span>
      <span class="finder-footer__actions">
        <el-button size="small" @click="dialogVisible = false">{{ $t("common.cancel") }}</el-button>
        <el-button size="small" plain type="primary" icon="el-icon-plus" @click="createNew">
          {{ $t("productFinder.createNew") }}
        </el-button>
      </span>
    </span>
  </el-dialog>
</template>

<script>
export default {
  name: "ProductFinderDialog",
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    // 已拥有商品名称集合（小写），用于标记「已添加」
    ownedNames: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      query: "",
      lastQuery: "",
      results: [],
      loading: false,
      touched: false,
      obtainingId: "",
      obtainedIds: [],
      debounceTimer: null
    };
  },
  computed: {
    dialogVisible: {
      get() {
        return this.visible;
      },
      set(v) {
        this.$emit("update:visible", v);
      }
    }
  },
  methods: {
    handleOpen() {
      this.query = "";
      this.lastQuery = "";
      this.results = [];
      this.touched = false;
      this.obtainedIds = [];
      this.$nextTick(() => {
        if (this.$refs.search) {
          this.$refs.search.focus();
        }
      });
    },
    handleClosed() {
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
        this.debounceTimer = null;
      }
    },
    formatPrice(v) {
      if (v === null || v === undefined || v === "") {
        return "$0.00";
      }
      const n = Number(v);
      return Number.isNaN(n) ? String(v) : `$${n.toFixed(2)}`;
    },
    isBarcodeLike(q) {
      return /^\d{6,}$/.test(String(q || "").trim());
    },
    isAdded(item) {
      if (!item) {
        return false;
      }
      if (item.added === true || item.obtained === true) {
        return true;
      }
      if (this.obtainedIds.indexOf(item.id) !== -1) {
        return true;
      }
      const name = String(item.name || "").trim().toLowerCase();
      return !!name && this.ownedNames.indexOf(name) !== -1;
    },
    onInput() {
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
      }
      this.debounceTimer = setTimeout(this.runSearch, 300);
    },
    runSearch() {
      const q = String(this.query || "").trim();
      this.lastQuery = q;
      if (!q) {
        this.touched = false;
        this.results = [];
        return;
      }
      this.touched = true;
      this.loading = true;
      // 后端主按 name 过滤；条码用客户端匹配兜底（后端加条码过滤后可直接用）。
      const params = { name: q, pageIndex: 1, pageSize: 24 };
      if (this.isBarcodeLike(q)) {
        params.goodsBarcode = q;
      }
      this.$api
        .request(this.$url.ProductLibrary.page, this.$method.get, params)
        .then(res => {
          this.loading = false;
          if (res.code === 0 && res.data) {
            let records = Array.isArray(res.data.records) ? res.data.records : [];
            records = records.map(this.normalize);
            if (this.isBarcodeLike(q)) {
              const exact = records.filter(r => String(r.goodsBarcode || "") === q);
              if (exact.length) {
                records = exact;
              }
            }
            this.results = records;
          } else {
            this.results = [];
          }
        })
        .catch(() => {
          this.loading = false;
          this.results = [];
        });
    },
    normalize(row) {
      return {
        ...row,
        name: row.name || row.goodsName || "",
        url: row.url || row.goodsUrl || "",
        costPrice: row.costPrice !== undefined && row.costPrice !== null ? row.costPrice : row.goodsPrice || "",
        goodsBarcode: row.goodsBarcode || row.barcode || ""
      };
    },
    useThis(item) {
      this.obtainingId = item.id;
      this.$api
        .request(this.$url.ProductLibrary.obtain, this.$method.post, { id: item.id })
        .then(res => {
          this.obtainingId = "";
          if (res.code === 0) {
            this.obtainedIds.push(item.id);
            this.$message.success(this.$t("productHub.addedToMine", { name: item.name }));
            this.$emit("obtained", item);
          } else {
            this.$message.warning(res.msg || this.$t("common.loadFailed"));
          }
        })
        .catch(err => {
          this.obtainingId = "";
          this.$message.error((err && err.msg) || this.$t("common.loadFailed"));
        });
    },
    createNew() {
      this.$emit("create-new", this.query);
      this.dialogVisible = false;
    }
  }
};
</script>

<style scoped lang="scss">
.finder-title {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  color: var(--vtm-ink, #303133);
}
.finder-title i {
  color: var(--vtm-accent, #04b98a);
}
.finder {
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.finder__lead {
  margin: 0;
  font-size: 13px;
  color: var(--vtm-ink-2, #486581);
}
.finder__results {
  min-height: 280px;
}
.finder__hint,
.finder__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-height: 280px;
  color: var(--vtm-ink-3, #829ab1);
  text-align: center;
}
.finder__hint i,
.finder__empty i {
  font-size: 40px;
  opacity: 0.6;
}
.finder__empty p {
  margin: 0;
  font-size: 14px;
  color: var(--vtm-ink-2, #486581);
}
.finder__empty-sub {
  font-size: 12.5px;
}
.finder-grid {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 440px;
  overflow-y: auto;
  padding: 2px;
}
.finder-card {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 10px 14px;
  border: 1px solid var(--vtm-line, #e4e7eb);
  border-radius: 8px;
  background: var(--vtm-surface, #fff);
}
.finder-card__img {
  width: 56px;
  height: 56px;
  flex: 0 0 56px;
  border-radius: 6px;
  overflow: hidden;
  background: var(--vtm-well, #f1f2f4);
  display: flex;
  align-items: center;
  justify-content: center;
}
.finder-card__img ::v-deep .el-image {
  width: 100%;
  height: 100%;
}
.finder-card__ph {
  color: var(--vtm-ink-3, #c0c4cc);
  font-size: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
}
.finder-card__body {
  flex: 1;
  min-width: 0;
}
.finder-card__name {
  font-size: 14px;
  font-weight: 600;
  color: var(--vtm-ink, #303133);
  line-height: 1.35;
  white-space: normal;
  word-break: break-word;
}
.finder-card__meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 4px;
}
.finder-card__price {
  font-size: 12.5px;
  font-weight: 600;
  color: var(--vtm-ok, #12805c);
  font-variant-numeric: tabular-nums;
}
.finder-card__code {
  font-size: 11px;
  color: var(--vtm-ink-3, #829ab1);
  font-family: var(--vtm-mono, monospace);
}
.finder-card__added {
  font-size: 12px;
  color: var(--vtm-ok, #12805c);
  display: inline-flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
}
.finder-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
.finder-footer__note {
  font-size: 12px;
  color: var(--vtm-ink-3, #829ab1);
}
.finder-footer__actions {
  display: inline-flex;
  gap: 8px;
}
::v-deep .product-finder .el-dialog__body {
  padding: 16px 20px;
}
</style>
